import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { numFormatter, numFormatters } from 'src/libs/helper'
import GaugeCharts from 'src/pages/Home/components/GaugeCharts'
import GradientButton from 'src/components/buttons/GradientButton'
import GradientWrapper from 'src/components/wrappers/GradientWrraper'
import PnLInsights from './components/PnLInsights'

import {
  deleteBotAction,
  selectAllPnlHistory,
  selectGunbotConfigPairs,
  selectGunbotPairsData,
  startGunbotAction,
  stopGunbotAction,
} from 'src/store/bots'
import { AppDispatch, RootState } from 'src/store'
import { BotRes } from 'src/store/bots/bots.api'
import AddBotDialog from './components/AddBotDialog'
import BotsTableComponent from './components/BotsTableComponent'
import EditBotDialog from './components/EditBotDialog'

type BalanceRow = {
  pair: string
  baseBalance: number
  quoteBalance: number
  bagValue: number
  isTotal?: boolean
}

// ===== Portfolio Card =====
function PortfolioValueCard({ convertedBalances }: { convertedBalances: Record<string, number> }) {
  const [selectedCurrency, setSelectedCurrency] = useState('USDT')
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const saved = localStorage.getItem('selectedCurrency')
    if (saved && convertedBalances[saved]) setSelectedCurrency(saved)
    else if (convertedBalances['USDT']) setSelectedCurrency('USDT')
    else if (Object.keys(convertedBalances).length > 0)
      setSelectedCurrency(Object.keys(convertedBalances)[0])
  }, [convertedBalances])

  useEffect(() => {
    if (selectedCurrency) localStorage.setItem('selectedCurrency', selectedCurrency)
  }, [selectedCurrency])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const tokens = Object.keys(convertedBalances).sort()
  const filteredTokens = tokens.filter((token) =>
    token.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const displayAssets = ['USDT', 'USDC', 'BNB', 'BTC', 'ETH']
  const formattedValue = (v: number) =>
    v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 8 })

  return (
    <div className="bg-[#0f0f0f] rounded-xl border border-[#2a2a2a] p-5 sm:p-6 mb-6 shadow-sm w-full max-w-3xl mx-auto hover:border-[#3a3a3a] transition-all duration-200">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="text-gray-300 text-sm font-medium">Portfolio Value</div>

        {/* Dropdown */}
        <div className="relative w-full sm:w-44" ref={dropdownRef}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="w-full bg-[#1a1a1a] border border-[#4E8FC6] text-gray-300 text-sm rounded-md px-3 py-2 flex justify-between items-center"
          >
            <span>{selectedCurrency}</span>
            <span className="text-gray-500">▼</span>
          </button>

          {isOpen && (
            <div className="absolute right-0 mt-1 w-full bg-[#1a1a1a] border border-[#4E8FC6] rounded-md shadow-lg z-10 p-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-full bg-[#111] border border-[#2a2a2a] rounded-md px-2 py-1 text-sm text-gray-300 mb-2 focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <div className="max-h-48 overflow-y-auto">
                {filteredTokens.length > 0 ? (
                  filteredTokens.map((token) => (
                    <div
                      key={token}
                      onClick={() => {
                        setSelectedCurrency(token)
                        setIsOpen(false)
                        setSearchTerm('')
                      }}
                      className={`px-2 py-1 rounded-md cursor-pointer text-sm ${
                        selectedCurrency === token
                          ? 'bg-[#2a2a2a] text-white'
                          : 'text-gray-300 hover:bg-[#222]'
                      }`}
                    >
                      {token}
                    </div>
                  ))
                ) : (
                  <div className="text-gray-500 text-xs px-2 py-1">No results</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Total Value */}
      <div className="mb-4">
        <div className="text-3xl sm:text-4xl font-semibold text-white">
          {formattedValue(convertedBalances[selectedCurrency] || 0)} {selectedCurrency}
        </div>
        <div className="text-sm text-gray-400">Converted total balance</div>
      </div>

      {/* Reference Assets */}
      <div>
        <div className="text-sm text-gray-400 mb-1">Reference Assets</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {displayAssets.map((token) => (
            <div
              key={token}
              className="bg-[#1a1a1a] border border-[#4E8FC6] rounded-md p-2 flex flex-col items-center"
            >
              <span className="text-white text-sm font-medium">{token}</span>
              <span className="text-gray-400 text-xs">
                {convertedBalances[token] ? formattedValue(convertedBalances[token]) : '—'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// ===== HomePage =====
export default function HomePage() {
  const dispatch = useDispatch<AppDispatch>()
  const allPnlHistory = useSelector(selectAllPnlHistory)
  const state = useSelector((s: RootState) => s)
  const numOfRunning = Object.keys(allPnlHistory).length

  const [section, setSection] = useState<'pnl' | 'balances' | 'analysis' | 'bots'>('bots')
  const [openAddBotDialog, setOpenAddBotDialog] = useState(false)
  const [editBot, setEditBot] = useState<BotRes>()
  const [selectedExchange, setSelectedExchange] = useState<string>('')

  const openAdd = useCallback(() => setOpenAddBotDialog(true), [])
  const closeAdd = useCallback(() => setOpenAddBotDialog(false), [])
  const openEdit = useCallback((bot: BotRes) => setEditBot(bot), [])
  const closeEdit = useCallback(() => setEditBot(undefined), [])
  const startGunbot = useCallback((bot: BotRes) => dispatch(startGunbotAction(bot)), [dispatch])
  const stopGunbot = useCallback((bot: BotRes) => dispatch(stopGunbotAction(bot)), [dispatch])
  const handleDeleteBot = useCallback(
    (bot: BotRes) => dispatch(deleteBotAction(bot.id)),
    [dispatch]
  )

  // === PnL totals ===
  const { today, yesterday, weekAgo, monthAgo, yearAgo, unit } = useMemo(() => {
    const data = { today: 0, yesterday: 0, weekAgo: 0, monthAgo: 0, yearAgo: 0, unit: 'USDT' }
    Object.values(allPnlHistory).forEach((pnl: any) => {
      data.today += pnl.today.pnl
      data.yesterday += pnl.yesterday.pnl
      data.weekAgo += pnl.weekAgo.pnl
      data.monthAgo += pnl.monthAgo.pnl
      data.yearAgo += pnl.yearAgo.pnl
    })
    return data
  }, [allPnlHistory])

  const todayGrow = today - yesterday
  const todayGrowPercent = yesterday ? ((today - yesterday) / Math.abs(yesterday)) * 100 : 0

  // === Balances grouped ===
  const groupedBalances = useMemo(() => {
    const groups: Record<
      string,
      { rows: BalanceRow[]; totals: { base: number; quote: number; bag: number } }
    > = {}
    Object.keys(allPnlHistory).forEach((url) => {
      const cfg = selectGunbotConfigPairs(url)(state)
      const data = selectGunbotPairsData(url)(state)
      if (!cfg || !data) return
      Object.keys(cfg).forEach((exchange) => {
        Object.keys(cfg[exchange]).forEach((pair) => {
          const key = `${exchange}/${pair}`
          const pd = data[key]
          if (!pd) return
          if (!groups[exchange]) groups[exchange] = { rows: [], totals: { base: 0, quote: 0, bag: 0 } }
          const base = pd.baseBalance ?? 0
          const quote = pd.quoteBalance ?? 0
          const bag = pd.bagvalue ?? 0
          groups[exchange].rows.push({ pair, baseBalance: base, quoteBalance: quote, bagValue: bag })
          groups[exchange].totals.base += base
          groups[exchange].totals.quote += quote
          groups[exchange].totals.bag += bag
        })
      })
    })
    Object.keys(groups).forEach((exchange) => {
      const { base, quote, bag } = groups[exchange].totals
      groups[exchange].rows.push({
        pair: 'TOTAL',
        baseBalance: base,
        quoteBalance: quote,
        bagValue: bag,
        isTotal: true,
      })
    })
    return groups
  }, [allPnlHistory, state])

  useEffect(() => {
    const exchanges = Object.keys(groupedBalances)
    if (exchanges.length > 0 && !selectedExchange) setSelectedExchange(exchanges[0])
  }, [groupedBalances, selectedExchange])

  // === Sorting ===
  const [sortConfig, setSortConfig] = useState<{ key: keyof BalanceRow; direction: 'asc' | 'desc' }>({
    key: 'pair',
    direction: 'asc',
  })

  const handleSort = (key: keyof BalanceRow) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const sortedRows = useMemo(() => {
    if (!selectedExchange || !groupedBalances[selectedExchange]) return []
    const rows = groupedBalances[selectedExchange].rows.filter((r) => !r.isTotal)
    return [...rows].sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1
      const valA = a[sortConfig.key]
      const valB = b[sortConfig.key]
      return typeof valA === 'string'
        ? valA.localeCompare(valB as string) * dir
        : ((valA as number) - (valB as number)) * dir
    })
  }, [groupedBalances, selectedExchange, sortConfig])

  const SortArrow = ({ col }: { col: keyof BalanceRow }) =>
    sortConfig.key !== col ? (
      <span className="text-gray-600 ml-1">↕</span>
    ) : sortConfig.direction === 'asc' ? (
      <span className="text-[#4E8FC6] ml-1">▲</span>
    ) : (
      <span className="text-[#4E8FC6] ml-1">▼</span>
    )

  // ===== UI =====
  return (
    <div className="flex h-screen w-full bg-[#1b1b1b] text-gray-200 overflow-hidden">
      {/* Sidebar (responsive) */}
<aside className="fixed md:relative md:static md:flex md:w-60 w-full bottom-0 md:bottom-auto bg-[#0d0d0d] border-t md:border-t-0 md:border-r border-[#222] flex md:flex-col md:p-5 z-30">
 
<nav className="flex flex-row md:flex-col justify-around md:justify-start w-full md:w-auto">
  {[
    { key: 'bots', label: 'Servers' },
    { key: 'pnl', label: 'PnL' },
    { key: 'balances', label: 'Balances' },
    { key: 'analysis', label: 'Analysis' },
    { key: 'separator', label: '________________________________' }, // Using a more descriptive key
    { key: 'version', label: 'Monit v9.3.3_beta' },   // Using a more descriptive key
  ].map((item) =>
    // If the key is 'separator' or 'version', render a styled div instead of a button
    item.key === 'separator' || item.key === 'version' ? (
      <div
        key={item.key}
        className={`px-4 py-3 w-full text-center md:text-left text-xs text-gray-600 ${
          // Hide the separator line on mobile for a cleaner look
          item.key === 'separator' ? 'hidden md:block' : ''
        }`}
      >
        {item.label}
      </div>
    ) : (
      // Otherwise, render the normal button
      <button
        key={item.key}
        onClick={() => setSection(item.key as any)}
        className={`px-4 py-3 text-base md:rounded-md transition w-full text-center md:text-left ${
          section === item.key
            ? 'bg-[#1b1b1b] text-white'
            : 'text-gray-400 hover:bg-[#161616]'
        }`}
      >
        {item.label}
      </button>
    )
  )}
</nav>
  
</aside>
      {/* Main */}
      <main className="flex flex-col flex-1 overflow-y-auto p-4 sm:p-6 md:p-10">
        {section === 'bots' && (
          <section>
            <GradientButton
              size="sm"
              className="w-full sm:w-auto mb-4 rounded-lg"
              onClick={openAdd}
            >
              <span className="mr-1 text-[19px]">+</span> Add Server
            </GradientButton>
            <BotsTableComponent
              onEditBot={openEdit}
              onDeleteBot={handleDeleteBot}
              onStartGunbot={startGunbot}
              onStopGunbot={stopGunbot}
            />
          </section>
        )}

        {section === 'pnl' && (
          <section>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-2">
              <GradientWrapper className="p-5 rounded-lg bg-[#161616] border border-[#2a2a2a]">
                <h6 className="text-2xl font-bold text-white">Today’s PnL</h6>
                <div className="text-gray-400 text-sm mt-1">
                  Total <span className="text-white">{numOfRunning}</span> bots
                </div>
                <h6 className="my-3 text-3xl font-bold text-white">
                  {numFormatters(today)} {unit}
                </h6>
              </GradientWrapper>

              <GradientWrapper className="p-5 rounded-lg bg-[#161616] border border-[#2a2a2a]">
                <h6 className="text-2xl font-bold text-white">PnL Stats</h6>
                <div className="text-md text-gray-400 mt-1">
                  Today {todayGrowPercent >= 0 ? 'better' : 'worse'} by{' '}
                  <span className="text-white">{numFormatters(todayGrow)} {unit}</span>
                </div>

                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { short: 'D', label: 'Yesterday', value: yesterday },
                    { short: 'W', label: 'Week', value: weekAgo },
                    { short: 'M', label: 'Month', value: monthAgo },
                    { short: 'Y', label: 'Year', value: yearAgo },
                  ].map(({ short, label, value }) => (
                    <div key={short} className="flex gap-2">
                      <GradientWrapper className="h-10 w-10 flex items-center justify-center rounded-md font-bold text-accent">
                        {short}
                      </GradientWrapper>
                      <div className="text-xs text-gray-400 flex flex-col justify-center">
                        <span>{label}</span>
                        <span className="text-sm text-white font-semibold">
                          {numFormatters(value)} {unit}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </GradientWrapper>
            </div>
            <PnLInsights />
          </section>
        )}

        {section === 'balances' && (
          <section>
            {/* Converted Portfolio */}
            {(() => {
  const allCoremems = state?.bots?.coremems || {}

  // Final result
  const convertedBalances: Record<string, number> = {}

  // We take only one snapshot per exchange
  const exchangeSnapshots: Record<string, Record<string, number>> = {}

  Object.values(allCoremems).forEach((coremem: any) => {
    const memory = coremem?.memory || {}

    Object.values(memory).forEach((pairData: any) => {
      const exchange = pairData?.exchangeName
      const snapshot = pairData?.convertedBalances

      if (!exchange || !snapshot) return

      // Save the FIRST snapshot for each exchange
      if (!exchangeSnapshots[exchange]) {
        exchangeSnapshots[exchange] = snapshot
      }
    })
  })

  // Now sum one snapshot per exchange
  Object.values(exchangeSnapshots).forEach((snapshot) => {
    for (const [symbol, value] of Object.entries(snapshot)) {
      const num = Number(value)
      if (!Number.isFinite(num)) continue
      convertedBalances[symbol] = (convertedBalances[symbol] || 0) + num
    }
  })

  return Object.keys(convertedBalances).length > 0 ? (
    <PortfolioValueCard convertedBalances={convertedBalances} />
  ) : null
})()}


            {/* Exchange Tabs */}
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.keys(groupedBalances).map((exchange) => (
                <button
                  key={exchange}
                  onClick={() => setSelectedExchange(exchange)}
                  className={`px-4 py-1.5 rounded-full text-sm ${
                    selectedExchange === exchange
                      ? 'bg-[#2a2a2a] text-[#4E8FC6]'
                      : 'bg-[#1a1a1a] text-gray-400 hover:bg-[#222]'
                  }`}
                >
                  {exchange}
                </button>
              ))}
            </div>

            {/* Balances Table */}
            {selectedExchange && groupedBalances[selectedExchange] && (
              <div className="w-full overflow-x-auto rounded-lg border border-[#2a2a2a] bg-[#101010] shadow-md">
                <table className="w-full text-xs sm:text-sm border-collapse">
                  <thead>
                    <tr className="bg-[#121212] border-b border-[#2a2a2a]">
                      {[
                        ['pair', 'Pair'],
                        ['baseBalance', 'Base'],
                        ['quoteBalance', 'Quote'],
                        ['bagValue', 'Bag Value'],
                      ].map(([col, label]) => (
                        <th
                          key={col}
                          onClick={() => handleSort(col as keyof BalanceRow)}
                          className="py-3 px-3 sm:px-4 text-left font-semibold text-gray-300 tracking-wide cursor-pointer select-none whitespace-nowrap"
                        >
                          {label} <SortArrow col={col as keyof BalanceRow} />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((row, i) => (
                      <tr key={i} className="border-b border-[#1a1a1a] hover:bg-[#181818]">
                        <td className="py-2 px-3 sm:px-4 text-white">{row.pair}</td>
                        <td className="py-2 px-3 sm:px-4 text-gray-300 font-mono">
                          {numFormatter(row.baseBalance)}
                        </td>
                        <td className="py-2 px-3 sm:px-4 text-gray-300 font-mono">
                          {numFormatter(row.quoteBalance)}
                        </td>
                        <td className="py-2 px-3 sm:px-4 text-gray-300 font-mono">
                          {numFormatter(row.bagValue)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {section === 'analysis' && (
          <section>
            <GaugeCharts />
          </section>
        )}
      </main>

      {/* Dialogs */}
      <AddBotDialog open={openAddBotDialog} onClose={closeAdd} />
      {editBot && <EditBotDialog open={!!editBot} bot={editBot} onClose={closeEdit} />}
    </div>
  )
}
